import { useState } from 'react';
import { useApp } from '../context/AppContext';
import { groupService, rateRuleService, promoCodeService, vehicleTypeService, vehicleService } from '../api/services';
import StatusPill from '../components/StatusPill';
import PageHeader from '../components/PageHeader';

// ── Confirm Dialog ────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  if (!message) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--sur)', borderRadius: 14, boxShadow: '0 8px 40px rgba(0,0,0,0.25)', padding: '28px 32px', minWidth: 320, maxWidth: 420, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>Are you sure?</div>
        <div style={{ fontSize: 12, color: 'var(--tx2)', marginBottom: 24 }}>{message}</div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

// ── Tab Button ────────────────────────────────────────────────────
function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
      background: 'none', color: active ? 'var(--ac)' : 'var(--tx2)',
      borderBottom: active ? '2px solid var(--ac)' : '2px solid transparent',
      marginBottom: -1, transition: 'all .12s', whiteSpace: 'nowrap',
    }}>
      {children}
    </button>
  );
}

// ── Count Pill ────────────────────────────────────────────────────
function Pill({ n, active }) {
  return (
    <span style={{
      marginLeft: 5, fontSize: 10,
      background: active ? 'var(--ac-l)' : 'var(--sur2)',
      color: active ? 'var(--ac)' : 'var(--tx3)',
      borderRadius: 10, padding: '1px 6px',
    }}>{n}</span>
  );
}

// ── Slide-in Form Card ────────────────────────────────────────────
function FormCard({ title, onCancel, onSave, saveLabel, children }) {
  return (
    <div style={{ background: 'var(--sur)', border: '1.5px solid var(--ac)', borderRadius: 12, padding: '20px 24px', marginBottom: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16, color: 'var(--ac)' }}>{title}</div>
      {children}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary" onClick={onSave}>{saveLabel || 'Save'}</button>
      </div>
    </div>
  );
}

// ── Toggle Switch ─────────────────────────────────────────────────
function Toggle({ value, onChange, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }} onClick={() => onChange(!value)}>
      <div style={{ width: 34, height: 18, borderRadius: 10, background: value ? 'var(--gr)' : 'var(--bd)', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 2, left: value ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
      </div>
      {label && <span style={{ fontSize: 11, color: 'var(--tx2)' }}>{label}</span>}
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────
function EmptyState({ icon, title, sub, action, onAction }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--tx3)' }}>
      <div style={{ fontSize: 44 }}>{icon}</div>
      <div style={{ marginTop: 12, fontSize: 13, fontWeight: 700, color: 'var(--tx)' }}>{title}</div>
      <div style={{ fontSize: 11, marginTop: 4, color: 'var(--tx2)' }}>{sub}</div>
      {action && <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={onAction}>{action}</button>}
    </div>
  );
}

// ── Insurance Plan inline form (used inside Model editor) ─────────
function InsPlanFormCard({ planForm, setPlanForm, onSave, onCancel }) {
  function PF(k) {
    return { value: planForm[k] ?? '', onChange: e => setPlanForm(p => ({ ...p, [k]: e.target.value })) };
  }
  return (
    <div style={{ border: '2px solid var(--ac)', borderRadius: 'var(--r)', padding: '12px 14px', background: 'var(--ac-l)', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div className="fg" style={{ margin: 0 }}>
        <label className="fl">Icon</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {['🛡️','💎','⭐','🏆','🎯','🌟','✨','🔐','🔵','🌈','🚘','💼'].map(ic => (
            <div key={ic} onClick={() => setPlanForm(p => ({ ...p, icon: ic }))}
              style={{ fontSize: 18, padding: '3px 7px', borderRadius: 6, cursor: 'pointer',
                border: `2px solid ${planForm.icon === ic ? 'var(--ac)' : 'var(--bd)'}`,
                background: planForm.icon === ic ? 'var(--ac-l)' : 'var(--sur)' }}>
              {ic}
            </div>
          ))}
        </div>
      </div>
      <div className="fg" style={{ margin: 0 }}>
        <label className="fl">Colour</label>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {['#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#f97316','#1e40af','#065f46'].map(c => (
            <div key={c} onClick={() => setPlanForm(p => ({ ...p, color: c }))}
              style={{ width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer',
                outline: planForm.color === c ? `3px solid ${c}` : '2px solid transparent',
                outlineOffset: 2, transition: 'outline .1s' }} />
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div className="fg" style={{ margin: 0 }}>
          <label className="fl">Plan name *</label>
          <input type="text" className="fi" placeholder="e.g. Full Cover" {...PF('name')} />
        </div>
        <div className="fg" style={{ margin: 0 }}>
          <label className="fl">Price / day (€)</label>
          <input type="number" className="fi" placeholder="0 = included" min="0" step="0.01" {...PF('price')} />
        </div>
        <div className="fg" style={{ margin: 0 }}>
          <label className="fl">Excess</label>
          <input type="text" className="fi" placeholder="e.g. €500 or Zero" {...PF('excess')} />
        </div>
        <div className="fg" style={{ margin: 0 }}>
          <label className="fl">Deposit (€)</label>
          <input type="number" className="fi" placeholder="0" min="0" {...PF('deposit')} />
        </div>
        <div className="fg" style={{ gridColumn: '1/-1', margin: 0 }}>
          <label className="fl">Description</label>
          <input type="text" className="fi" placeholder="e.g. Full cover, zero excess, unlimited mileage" {...PF('desc')} />
        </div>
        <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setPlanForm(p => ({ ...p, recommended: !p.recommended }))}>
          <input type="checkbox" checked={!!planForm.recommended} readOnly style={{ accentColor: 'var(--ac)' }} />
          <span style={{ fontSize: 11, color: 'var(--tx2)' }}>Mark as recommended plan</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={onSave}>Save Plan</button>
      </div>
    </div>
  );
}

// ── Constants ─────────────────────────────────────────────────────
const GROUP_ICONS = ['🚗','🚕','🚙','🚐','🚌','🚚','🛻','🏗️','🚜','⭐','💎','🔥','🏖️','🏔️','🎯','🌟'];
const COLORS = ['#6366f1','#22c55e','#f59e0b','#ef4444','#06b6d4','#ec4899','#8b5cf6','#14b8a6','#f97316','#64748b'];
const BLANK_GROUP = { name: '', icon: '🚗', color: '#6366f1', priceDay: '', showOnBooking: true, minDays: 1, agreementId: '' };
const BLANK_RULE  = { name: '', appliesTo: 'all', type: 'duration', minDays: 7, dateStart: '', dateEnd: '', effect: 'discount', valueType: 'percent', value: '', label: '', showLabel: true, active: true };
const BLANK_PROMO = { code: '', discountType: 'percent', discountValue: '', usageLimit: '', note: '', expiryDate: '', active: true };
const TYPE_ICONS  = ['🚗','🚕','🚙','🚐','🚌','🚚','🚛','🛻','🏗️','🚜','🚑','🚒','🚤','🏍️','🛵','🚁','🏕️','⛵','🚎','🚃','🏎️','🚲','🛺','🛥️','🚀','🛶','🦽','🛤️','⛷️','🏄'];
const BLANK_TYPE  = { name: '', icon: '🚗' };
const BLANK_MODEL = {
  name: '', icon: '🚗', type: 'Car',
  photos: [],            // base64 up to 5
  features: '',
  transmission: 'Automatic',
  fuelType: 'Petrol',
  seats: '5 seats',
  mileage: 'Unlimited',
  priceDay: '',
  deposit: '',
  contractId: '',
  groupId: '',
  confirm: 'manual',     // manual | auto — applies to all vehicles in this model
  insuranceMode: 'standard',
  customInsurance: [],   // array of { id, icon, name, color, price, excess, deposit, desc, recommended }
};
const TRANSMISSIONS = ['Automatic', 'Manual'];
const FUEL_TYPES    = ['Petrol', 'Diesel', 'Hybrid', 'Electric', 'LPG'];
const SEATS_OPTS    = ['2 seats', '4 seats', '5 seats', '7 seats', '9 seats'];
const MILEAGE_OPTS  = ['Unlimited', '100 km/day', '200 km/day', '300 km/day'];
const MAX_MODEL_PHOTOS = 5;
const INS_PLAN_ICONS  = ['🛡️','💎','⭐','🏆','🎯','🌟','✨','🔐','🔵','🌈','🚘','💼'];
const INS_PLAN_COLORS = ['#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#f97316','#1e40af','#065f46'];
const BLANK_INS_PLAN = { id: '', icon: '🛡️', name: '', color: '#0ea5e9', price: '', excess: '', deposit: '', desc: '', recommended: false };

// ─────────────────────────────────────────────────────────────────
export default function FleetPage() {
  const {
    vehicles, setVehicles, rentals, openModal, showToast, setDetailPanel,
    groups, setGroups, rateRules, setRateRules, promoCodes, setPromoCodes,
    vehicleTypes, setVehicleTypes,
    vehicleModels, setVehicleModels,
    areas, transferVehicle, locationLog,
    agreementTemplates,
  } = useApp();

  const [tab, setTab] = useState('vehicles');

  // Vehicles filters
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterBooking, setFilterBooking] = useState(false);

  // Transfer dialog
  const [transferDlg, setTransferDlg] = useState(null);

  // Confirm dialog
  const [confirmDlg, setConfirmDlg] = useState(null);
  function askConfirm(msg, fn) { setConfirmDlg({ message: msg, onConfirm: fn }); }
  function closeConfirm() { setConfirmDlg(null); }

  // Groups form
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupForm, setGroupForm] = useState(BLANK_GROUP);
  const [editingGroupId, setEditingGroupId] = useState(null);

  // Rate rules form
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleForm, setRuleForm] = useState(BLANK_RULE);
  const [editingRuleId, setEditingRuleId] = useState(null);

  // Promo codes form
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoForm, setPromoForm] = useState(BLANK_PROMO);
  const [editingPromoId, setEditingPromoId] = useState(null);

  // Vehicle types form
  const [showTypeForm, setShowTypeForm] = useState(false);
  const [typeForm, setTypeForm] = useState(BLANK_TYPE);
  const [editingTypeId, setEditingTypeId] = useState(null);

  // Vehicle models form
  const [showModelForm, setShowModelForm] = useState(false);
  const [modelForm, setModelForm] = useState(BLANK_MODEL);
  const [editingModelId, setEditingModelId] = useState(null);
  const [expandedModelId, setExpandedModelId] = useState(null);

  function unassignVehicleFromModel(vid) {
    askConfirm('Remove this vehicle from the model? It will become an individual vehicle (you may need to set its price, photos, etc. afterwards).', () => {
      setVehicles(prev => prev.map(v => v.id === vid ? { ...v, modelId: null } : v));
      closeConfirm();
      showToast('Vehicle unassigned from model');
    });
  }

  // ── Model insurance plan editing ─────────────────────────────────────────
  const [insPlanEdit, setInsPlanEdit] = useState(null); // null | 'new' | index
  const [insPlanForm, setInsPlanForm] = useState({ ...BLANK_INS_PLAN });

  function openNewInsPlan() {
    setInsPlanForm({ ...BLANK_INS_PLAN, id: 'mi-' + Date.now() });
    setInsPlanEdit('new');
  }
  function openEditInsPlan(idx) {
    setInsPlanForm({ ...modelForm.customInsurance[idx] });
    setInsPlanEdit(idx);
  }
  function saveInsPlan() {
    if (!insPlanForm.name.trim()) { showToast('Plan name is required'); return; }
    const plans = [...(modelForm.customInsurance || [])];
    if (insPlanEdit === 'new') plans.push({ ...insPlanForm });
    else plans[insPlanEdit] = { ...insPlanForm };
    setModelForm(p => ({ ...p, customInsurance: plans }));
    setInsPlanEdit(null);
  }
  function deleteInsPlan(idx) {
    setModelForm(p => ({ ...p, customInsurance: p.customInsurance.filter((_, j) => j !== idx) }));
    if (insPlanEdit === idx) setInsPlanEdit(null);
  }

  function openAddModel() {
    setModelForm(BLANK_MODEL);
    setEditingModelId(null);
    setShowModelForm(true);
  }
  function openEditModel(m) {
    setModelForm({ ...BLANK_MODEL, ...m });
    setEditingModelId(m.id);
    setShowModelForm(true);
  }
  function cancelModelForm() {
    setShowModelForm(false);
    setEditingModelId(null);
    setModelForm(BLANK_MODEL);
    setInsPlanEdit(null);
  }
  function saveModel() {
    if (!modelForm.name.trim()) { showToast('Model name is required'); return; }
    if (editingModelId) {
      setVehicleModels(prev => prev.map(m => m.id === editingModelId ? { ...m, ...modelForm } : m));
      showToast('Model updated');
    } else {
      const id = 'm-' + Date.now().toString(36);
      setVehicleModels(prev => [...prev, { ...modelForm, id }]);
      showToast('Model created');
    }
    cancelModelForm();
  }
  function deleteModel(id) {
    const used = vehicles.filter(v => v.modelId === id).length;
    if (used > 0) { showToast(`Cannot delete — ${used} vehicle(s) use this model`); return; }
    askConfirm('Delete this model? This cannot be undone.', () => {
      setVehicleModels(prev => prev.filter(m => m.id !== id));
      closeConfirm();
      showToast('Model deleted');
    });
  }
  function onModelPhotoChange(e, idx) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setModelForm(p => {
        const photos = [...(p.photos || [])];
        photos[idx] = ev.target.result;
        return { ...p, photos };
      });
    };
    reader.readAsDataURL(file);
  }
  function removeModelPhoto(idx) {
    setModelForm(p => {
      const photos = [...(p.photos || [])];
      photos.splice(idx, 1);
      return { ...p, photos };
    });
  }

  // Helpers
  const getGroup = (gid) => groups.find(g => g.id === gid);
  const vehiclesInGroup = (gid) => vehicles.filter(v => v.groupId === gid).length;
  const getRental = (vid) => rentals.find(r => r.vid === vid && ['active','overdue','booked'].includes(r.status));
  const getEffectivePrice = (v) => {
    // 1) Model price wins when assigned
    if (v.modelId) {
      const m = (vehicleModels || []).find(mm => mm.id === v.modelId);
      if (m && m.priceDay !== '' && m.priceDay != null) return parseFloat(m.priceDay) || 0;
    }
    // 2) Group price
    if (v.useGroupPricing && v.groupId) {
      const g = getGroup(v.groupId);
      return g ? g.priceDay : v.priceDay;
    }
    // 3) Individual price
    return v.priceDay;
  };

  // ── Group handlers ────────────────────────────────────────────
  function openAddGroup() { setGroupForm(BLANK_GROUP); setEditingGroupId(null); setShowGroupForm(true); }
  function openEditGroup(g) { setGroupForm({ name: g.name, icon: g.icon, color: g.color, priceDay: g.priceDay, showOnBooking: g.showOnBooking, minDays: g.minDays ?? 1, agreementId: g.agreementId || '' }); setEditingGroupId(g.id); setShowGroupForm(true); }
  function cancelGroupForm() { setShowGroupForm(false); setEditingGroupId(null); setGroupForm(BLANK_GROUP); }
  function saveGroup() {
    if (!groupForm.name.trim()) { showToast('Group name required'); return; }
    const parsed = { ...groupForm, priceDay: parseFloat(groupForm.priceDay) || 0, minDays: parseInt(groupForm.minDays) || 1 };
    if (editingGroupId) {
      setGroups(gs => gs.map(g => g.id === editingGroupId ? { ...g, ...parsed } : g));
      const grp = groups.find(g => g.id === editingGroupId);
      groupService.update(grp?._backendId || editingGroupId, parsed).catch(() => {});
      showToast('✓ Group updated');
    } else {
      const tempId = 'g' + Date.now();
      groupService.create({ ...parsed, id: tempId }).then(created => {
        setGroups(gs => [...gs, created]);
      }).catch(() => {
        setGroups(gs => [...gs, { ...parsed, id: tempId }]);
      });
      showToast('✓ Group created');
    }
    cancelGroupForm();
  }
  function deleteGroup(id) {
    const cnt = vehiclesInGroup(id);
    askConfirm(`Delete this group?${cnt > 0 ? ` ${cnt} vehicle${cnt !== 1 ? 's' : ''} will become ungrouped.` : ''}`, () => {
      const grp = groups.find(g => g.id === id);
      setGroups(gs => gs.filter(g => g.id !== id));
      setVehicles(vs => vs.map(v => v.groupId === id ? { ...v, groupId: null, useGroupPricing: false } : v));
      groupService.delete(grp?._backendId || id).catch(() => {});
      showToast('Group deleted');
      closeConfirm();
    });
  }

  // ── Rate rule handlers ────────────────────────────────────────
  function openAddRule() { setRuleForm(BLANK_RULE); setEditingRuleId(null); setShowRuleForm(true); }
  function openEditRule(r) { setRuleForm({ ...r }); setEditingRuleId(r.id); setShowRuleForm(true); }
  function cancelRuleForm() { setShowRuleForm(false); setEditingRuleId(null); setRuleForm(BLANK_RULE); }
  function saveRule() {
    if (!ruleForm.name.trim() || !ruleForm.value) { showToast('Name and value required'); return; }
    const parsed = { ...ruleForm, minDays: parseInt(ruleForm.minDays) || 1, value: parseFloat(ruleForm.value) };
    if (editingRuleId) {
      setRateRules(rs => rs.map(r => r.id === editingRuleId ? { ...r, ...parsed } : r));
      const rule = rateRules.find(r => r.id === editingRuleId);
      rateRuleService.update(rule?._backendId || editingRuleId, parsed).catch(() => {});
      showToast('✓ Rule updated');
    } else {
      const tempId = 'rr' + Date.now();
      rateRuleService.create({ ...parsed, id: tempId }).then(created => {
        setRateRules(rs => [...rs, created]);
      }).catch(() => {
        setRateRules(rs => [...rs, { ...parsed, id: tempId }]);
      });
      showToast('✓ Rate rule created');
    }
    cancelRuleForm();
  }
  function deleteRule(id) {
    askConfirm('Delete this rate rule?', () => {
      const rule = rateRules.find(r => r.id === id);
      setRateRules(rs => rs.filter(r => r.id !== id));
      rateRuleService.delete(rule?._backendId || id).catch(() => {});
      showToast('Rule deleted');
      closeConfirm();
    });
  }
  function toggleRuleActive(id) {
    setRateRules(rs => rs.map(r => r.id === id ? { ...r, active: !r.active } : r));
    const rule = rateRules.find(r => r.id === id);
    if (rule) rateRuleService.update(rule._backendId || id, { ...rule, active: !rule.active }).catch(() => {});
  }

  // ── Promo code handlers ───────────────────────────────────────
  function openAddPromo() { setPromoForm(BLANK_PROMO); setEditingPromoId(null); setShowPromoForm(true); }
  function openEditPromo(p) { setPromoForm({ ...p, usageLimit: p.usageLimit ?? '' }); setEditingPromoId(p.id); setShowPromoForm(true); }
  function cancelPromoForm() { setShowPromoForm(false); setEditingPromoId(null); setPromoForm(BLANK_PROMO); }
  function savePromo() {
    if (!promoForm.code.trim() || !promoForm.discountValue) { showToast('Code and discount value required'); return; }
    const parsed = { ...promoForm, discountValue: parseFloat(promoForm.discountValue), usageLimit: promoForm.usageLimit ? parseInt(promoForm.usageLimit) : null };
    if (editingPromoId) {
      setPromoCodes(ps => ps.map(p => p.id === editingPromoId ? { ...p, ...parsed } : p));
      const promo = promoCodes.find(p => p.id === editingPromoId);
      promoCodeService.update(promo?._backendId || editingPromoId, parsed).catch(() => {});
      showToast('✓ Promo code updated');
    } else {
      const tempId = 'pc' + Date.now();
      promoCodeService.create({ ...parsed, id: tempId, usedCount: 0 }).then(created => {
        setPromoCodes(ps => [...ps, created]);
      }).catch(() => {
        setPromoCodes(ps => [...ps, { ...parsed, id: tempId, usedCount: 0 }]);
      });
      showToast('✓ Promo code created');
    }
    cancelPromoForm();
  }
  function deletePromo(id) {
    askConfirm('Delete this promo code?', () => {
      const promo = promoCodes.find(p => p.id === id);
      setPromoCodes(ps => ps.filter(p => p.id !== id));
      promoCodeService.delete(promo?._backendId || id).catch(() => {});
      showToast('Promo code deleted');
      closeConfirm();
    });
  }
  function togglePromoActive(id) { setPromoCodes(ps => ps.map(p => p.id === id ? { ...p, active: !p.active } : p)); }

  // ── Vehicle type handlers ─────────────────────────────────────
  function openAddType()   { setTypeForm(BLANK_TYPE); setEditingTypeId(null); setShowTypeForm(true); }
  function openEditType(t) { setTypeForm({ name: t.name, icon: t.icon }); setEditingTypeId(t.id); setShowTypeForm(true); }
  function cancelTypeForm() { setShowTypeForm(false); setEditingTypeId(null); setTypeForm(BLANK_TYPE); }
  function saveType() {
    if (!typeForm.name.trim()) { showToast('Type name required'); return; }
    if (editingTypeId) {
      setVehicleTypes(ts => ts.map(t => t.id === editingTypeId ? { ...t, ...typeForm } : t));
      const oldType = vehicleTypes.find(t => t.id === editingTypeId);
      if (oldType && oldType.name !== typeForm.name) {
        setVehicles(vs => vs.map(v => v.type === oldType.name ? { ...v, type: typeForm.name } : v));
      }
      vehicleTypeService.update(oldType?._backendId || editingTypeId, typeForm).catch(() => {});
      showToast('✓ Type updated');
    } else {
      vehicleTypeService.create(typeForm).then(created => {
        setVehicleTypes(ts => [...ts, created]);
      }).catch(() => {
        const id = typeForm.name.toLowerCase().replace(/[^a-z0-9]/g, '') + Date.now();
        setVehicleTypes(ts => [...ts, { ...typeForm, id }]);
      });
      showToast('✓ Type created');
    }
    cancelTypeForm();
  }
  function deleteType(id) {
    const t = vehicleTypes.find(x => x.id === id);
    const cnt = vehicles.filter(v => v.type === t?.name).length;
    if (cnt > 0) { showToast(`Cannot delete — ${cnt} vehicle${cnt !== 1 ? 's' : ''} use this type`); return; }
    askConfirm(`Delete type "${t?.name}"?`, () => {
      setVehicleTypes(ts => ts.filter(x => x.id !== id));
      vehicleTypeService.delete(t?._backendId || id).catch(() => {});
      showToast('Type deleted');
      closeConfirm();
    });
  }

  // ── Vehicles filtered list ────────────────────────────────────
  const filtered = vehicles.filter(v => {
    const q = search.toLowerCase();
    const matchQ = !q || v.name.toLowerCase().includes(q) || v.plate.toLowerCase().includes(q);
    const matchT = !filterType || v.type === filterType;
    const matchA = !filterArea || v.currentAreaId === filterArea;
    const matchB = !filterBooking || v.bookPage;
    return matchQ && matchT && matchA && matchB;
  });

  // ── Display helpers ───────────────────────────────────────────
  function ruleConditionLabel(r) {
    if (r.type === 'duration') return `${r.minDays}+ days`;
    if (r.type === 'daterange') return `${r.dateStart || '?'} → ${r.dateEnd || '?'}`;
    return `${r.minDays}+ days & date range`;
  }
  function ruleEffectLabel(r) {
    const sign = r.effect === 'discount' ? '−' : '+';
    return `${sign}${r.value}${r.valueType === 'percent' ? '%' : '€'}`;
  }
  function ruleAppliesToLabel(r) {
    if (r.appliesTo === 'all') return 'All vehicles';
    const g = getGroup(r.appliesTo);
    return g ? `${g.icon} ${g.name}` : r.appliesTo;
  }

  return (
    <div>
      <PageHeader
        title="Fleet & Pricing"
        icon="🚗"
        subtitle="Manage vehicles, models, groups, rate rules, promo codes, vehicle types, and area transfers."
      />
      <ConfirmDialog message={confirmDlg?.message} onConfirm={confirmDlg?.onConfirm} onCancel={closeConfirm} />

      {/* ── Transfer Dialog ──────────────────────────────────── */}
      {transferDlg && (() => {
        const tv = transferDlg.vehicle;
        const curArea = areas.find(a => a.id === tv.currentAreaId);
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'var(--sur)', borderRadius: 14, boxShadow: '0 8px 40px rgba(0,0,0,0.25)', padding: '24px 28px', minWidth: 340, maxWidth: 420 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16 }}>Transfer Vehicle</div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{tv.icon} {tv.name}</div>
              <div style={{ fontSize: 11, color: 'var(--tx2)', marginBottom: 14 }}>{tv.plate}</div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)', display: 'block', marginBottom: 4 }}>Current Area</label>
                <div style={{ fontSize: 12, padding: '6px 10px', background: 'var(--sur2)', borderRadius: 8, border: '1px solid var(--bd)', color: 'var(--tx)' }}>
                  {curArea ? `${curArea.icon} ${curArea.name}` : 'No area assigned'}
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)', display: 'block', marginBottom: 4 }}>Destination Area</label>
                <select className="fsel" style={{ width: '100%' }} value={transferDlg.toAreaId} onChange={e => setTransferDlg(d => ({ ...d, toAreaId: e.target.value }))}>
                  <option value="">Select area...</option>
                  {areas.filter(a => a.id !== tv.currentAreaId).map(a => (
                    <option key={a.id} value={a.id}>{a.icon} {a.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setTransferDlg(null)}>Cancel</button>
                <button className="btn btn-primary" disabled={!transferDlg.toAreaId} onClick={() => {
                  transferVehicle(tv.id, transferDlg.toAreaId, 'manual_transfer');
                  const dest = areas.find(a => a.id === transferDlg.toAreaId);
                  showToast(`✓ ${tv.name} transferred to ${dest?.name || 'new area'}`);
                  setTransferDlg(null);
                }}>Confirm Transfer</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Tab Bar ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--bd)', marginBottom: 18 }}>
        <TabBtn active={tab === 'vehicles'} onClick={() => setTab('vehicles')}>🚗 Vehicles<Pill n={vehicles.length} active={tab === 'vehicles'} /></TabBtn>
        <TabBtn active={tab === 'models'}   onClick={() => setTab('models')}>🏷️ Models<Pill n={vehicleModels.length} active={tab === 'models'} /></TabBtn>
        <TabBtn active={tab === 'groups'}   onClick={() => setTab('groups')}>📁 Groups<Pill n={groups.length} active={tab === 'groups'} /></TabBtn>
        <TabBtn active={tab === 'types'}    onClick={() => setTab('types')}>🚙 Types<Pill n={vehicleTypes.length} active={tab === 'types'} /></TabBtn>
        <TabBtn active={tab === 'rules'}    onClick={() => setTab('rules')}>📊 Rate Rules<Pill n={rateRules.length} active={tab === 'rules'} /></TabBtn>
        <TabBtn active={tab === 'promos'}   onClick={() => setTab('promos')}>🎟️ Promo Codes<Pill n={promoCodes.length} active={tab === 'promos'} /></TabBtn>
        <TabBtn active={tab === 'transfers'} onClick={() => setTab('transfers')}>📍 Transfers<Pill n={locationLog.length} active={tab === 'transfers'} /></TabBtn>
      </div>

      {/* ══════════════ VEHICLES TAB ═══════════════════════════ */}
      {tab === 'vehicles' && (
        <div>
          <div className="tb2">
            <div className="srch">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              <input type="text" placeholder="Search vehicle, plate..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="fsel" value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">All types</option>
              <option value="Car">Cars</option>
              <option value="Van">Vans</option>
              <option value="Machine">Machines</option>
            </select>
            <select className="fsel" value={filterArea} onChange={e => setFilterArea(e.target.value)}>
              <option value="">All areas</option>
              {areas.map(a => <option key={a.id} value={a.id}>{a.icon} {a.name}</option>)}
            </select>
            <button onClick={() => setFilterBooking(b => !b)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              border: filterBooking ? '1.5px solid var(--gr)' : '1px solid var(--bd)',
              background: filterBooking ? 'var(--gr-l)' : 'var(--sur)',
              color: filterBooking ? 'var(--gr)' : 'var(--tx2)',
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: filterBooking ? 'var(--gr)' : 'var(--bd)' }} />
              Booking page
            </button>
            <div style={{ marginLeft: 'auto' }} />
            <button className="btn btn-primary" onClick={() => openModal('add-vehicle')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              Add Vehicle
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 12 }}>
            {filtered.map(v => {
              const rental = getRental(v.id);
              const group = v.groupId ? getGroup(v.groupId) : null;
              const effectivePrice = getEffectivePrice(v);
              return (
                <div key={v.id} className="vcard" style={{
                  background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 'var(--rl)',
                  overflow: 'hidden', boxShadow: 'var(--sh)', cursor: 'pointer', transition: 'all .12s',
                }}
                  onMouseOver={e => e.currentTarget.style.boxShadow = 'var(--sh2)'}
                  onMouseOut={e => e.currentTarget.style.boxShadow = 'var(--sh)'}
                >
                  <div style={{ height: 90, background: 'var(--sur3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, position: 'relative', borderBottom: '1px solid var(--bd)', overflow: 'hidden' }}>
                    {v.photos?.length > 0
                      ? <img src={v.photos[0]} alt={v.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span>{v.icon}</span>
                    }
                    <div style={{ position: 'absolute', top: 8, right: 8 }}><StatusPill status={v.status} /></div>
                    <div title={v.bookPage ? 'On booking page' : 'Not on booking page'} style={{ position: 'absolute', top: 8, left: 8, width: 10, height: 10, borderRadius: '50%', background: v.bookPage ? 'var(--gr)' : 'var(--ac)', border: '2px solid var(--sur)' }} />
                    {v.photos?.length > 1 && (
                      <div style={{ position: 'absolute', bottom: 5, right: 8, fontSize: 9, background: 'rgba(0,0,0,.5)', color: '#fff', borderRadius: 8, padding: '1px 6px' }}>
                        1/{v.photos.length}
                      </div>
                    )}
                    {group && (
                      <div style={{ position: 'absolute', bottom: 5, left: 8, fontSize: 9, fontWeight: 700, color: '#fff', background: group.color, borderRadius: 4, padding: '1px 5px' }}>
                        {group.icon} {group.name}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '10px 12px' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{v.name.split('–')[0].trim()}</div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace", marginBottom: 4 }}>{v.type} · {v.plate}</div>
                    {(() => { const area = areas.find(a => a.id === v.currentAreaId); return area ? (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 600, color: area.color, background: area.color + '18', border: `1px solid ${area.color}33`, borderRadius: 4, padding: '1px 6px', marginBottom: 4 }}>
                        <span>📍</span> {area.name}
                      </div>
                    ) : null; })()}
                    <div style={{ fontSize: 10, color: 'var(--tx2)', marginBottom: 6 }}>{v.seats} · {v.features}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
                      {effectivePrice && (
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--bl)', fontFamily: "'DM Mono',monospace" }}>€{effectivePrice}/day</div>
                      )}
                      {v.useGroupPricing && group && (
                        <div style={{ fontSize: 9, color: group.color, background: group.color + '22', borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>group rate</div>
                      )}
                    </div>
                    {rental && (
                      <div style={{ fontSize: 10, background: 'var(--sur2)', border: '1px solid var(--bd)', borderRadius: 5, padding: '5px 8px', marginBottom: 6, color: 'var(--tx2)' }}>
                        <div style={{ fontWeight: 500 }}>{rental.customer}</div>
                        <div style={{ color: 'var(--tx3)' }}>Until {rental.endLabel}</div>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--tx3)', marginBottom: 8 }}>
                      <span>Deposit: €{v.deposit}</span>
                      <span style={{ textTransform: 'capitalize' }}>Confirm: {v.confirm}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-xs" style={{ flex: 1, justifyContent: 'center' }} onClick={() => setDetailPanel({ open: true, vid: v.id })}>View</button>
                      <button className="btn btn-xs btn-blue" style={{ flex: 1, justifyContent: 'center' }} onClick={() => openModal('add-vehicle', v.id)}>Edit</button>
                      <button className="btn btn-ghost btn-xs" style={{ flex: 1, justifyContent: 'center', fontSize: 9 }} onClick={e => { e.stopPropagation(); setTransferDlg({ vehicle: v, toAreaId: '' }); }}>Transfer</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ══════════════ MODELS TAB ═════════════════════════════ */}
      {tab === 'models' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Vehicle Models</div>
              <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>
                Define each model (e.g. Toyota Corolla) once with photos, features and price.
                Then assign vehicles to a model — they inherit everything except plate &amp; status.
                Plates are hidden on the public booking page.
              </div>
            </div>
            {!showModelForm && (
              <button className="btn btn-primary" style={{ flexShrink: 0, marginLeft: 16 }} onClick={openAddModel}>+ Add Model</button>
            )}
          </div>

          {showModelForm && (
            <FormCard title={editingModelId ? '✏️ Edit Model' : '➕ New Model'} onCancel={cancelModelForm} onSave={saveModel} saveLabel={editingModelId ? 'Save Changes' : 'Create Model'}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

                {/* Name + Icon + Type row */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Model name *</label>
                    <input type="text" className="fi" placeholder="e.g. Toyota Corolla Hybrid"
                      value={modelForm.name} onChange={e => setModelForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Icon</label>
                    <select className="fi" value={modelForm.icon} onChange={e => setModelForm(f => ({ ...f, icon: e.target.value }))}>
                      {TYPE_ICONS.map(ic => <option key={ic} value={ic}>{ic}</option>)}
                    </select>
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Type</label>
                    <select className="fi" value={modelForm.type} onChange={e => setModelForm(f => ({ ...f, type: e.target.value }))}>
                      {vehicleTypes.map(t => <option key={t.id} value={t.name}>{t.icon} {t.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Photos row — 5 slots */}
                <div>
                  <label className="fl">Photos ({(modelForm.photos || []).filter(Boolean).length}/{MAX_MODEL_PHOTOS}) — shown on booking page</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                    {Array.from({ length: MAX_MODEL_PHOTOS }, (_, idx) => {
                      const src = (modelForm.photos || [])[idx];
                      return (
                        <div key={idx} style={{ position: 'relative', aspectRatio: '4/3', border: `2px dashed ${src ? 'transparent' : 'var(--bd)'}`, borderRadius: 8, background: src ? 'transparent' : 'var(--sur2)', overflow: 'hidden' }}>
                          {src ? (
                            <>
                              <img src={src} alt={`Photo ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                              {idx === 0 && (
                                <div style={{ position: 'absolute', top: 4, left: 4, fontSize: 9, padding: '2px 6px', background: 'var(--ac)', color: '#fff', borderRadius: 4, fontWeight: 700 }}>COVER</div>
                              )}
                              <button type="button" onClick={() => removeModelPhoto(idx)} style={{ position: 'absolute', top: 4, right: 4, width: 22, height: 22, border: 'none', borderRadius: '50%', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', fontSize: 11 }}>✕</button>
                            </>
                          ) : (
                            <label style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 10, color: 'var(--tx3)' }}>
                              <input type="file" accept="image/*" onChange={(e) => onModelPhotoChange(e, idx)} style={{ display: 'none' }} />
                              <div style={{ fontSize: 20 }}>📷</div>
                              <div>{idx === 0 ? 'Cover' : 'Photo ' + (idx + 1)}</div>
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Specs row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Transmission</label>
                    <select className="fi" value={modelForm.transmission} onChange={e => setModelForm(f => ({ ...f, transmission: e.target.value }))}>
                      {TRANSMISSIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Fuel Type</label>
                    <select className="fi" value={modelForm.fuelType} onChange={e => setModelForm(f => ({ ...f, fuelType: e.target.value }))}>
                      {FUEL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Seats</label>
                    <select className="fi" value={modelForm.seats} onChange={e => setModelForm(f => ({ ...f, seats: e.target.value }))}>
                      {SEATS_OPTS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Mileage</label>
                    <select className="fi" value={modelForm.mileage} onChange={e => setModelForm(f => ({ ...f, mileage: e.target.value }))}>
                      {MILEAGE_OPTS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                {/* Features */}
                <div className="fg" style={{ margin: 0 }}>
                  <label className="fl">Features (comma-separated)</label>
                  <input type="text" className="fi" placeholder="A/C, GPS, Bluetooth, USB, Cruise control"
                    value={modelForm.features} onChange={e => setModelForm(f => ({ ...f, features: e.target.value }))} />
                </div>

                {/* Price + Deposit + Contract + Group + Confirmation row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 10 }}>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Price / day (€) *</label>
                    <input type="number" className="fi" placeholder="45"
                      value={modelForm.priceDay} onChange={e => setModelForm(f => ({ ...f, priceDay: e.target.value }))} />
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Deposit (€)</label>
                    <input type="number" className="fi" placeholder="500"
                      value={modelForm.deposit} onChange={e => setModelForm(f => ({ ...f, deposit: e.target.value }))} />
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Contract template</label>
                    <select className="fi" value={modelForm.contractId} onChange={e => setModelForm(f => ({ ...f, contractId: e.target.value }))}>
                      <option value="">— Default —</option>
                      {(agreementTemplates || []).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Group (rate rules)</label>
                    <select className="fi" value={modelForm.groupId} onChange={e => setModelForm(f => ({ ...f, groupId: e.target.value }))}>
                      <option value="">— None —</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.icon} {g.name}</option>)}
                    </select>
                  </div>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Confirmation</label>
                    <select className="fi" value={modelForm.confirm} onChange={e => setModelForm(f => ({ ...f, confirm: e.target.value }))}>
                      <option value="manual">Manual</option>
                      <option value="auto">Auto</option>
                    </select>
                  </div>
                </div>

                {/* ── Insurance ─────────────────────────────────────────── */}
                <div style={{ border: '1px solid var(--bd)', borderRadius: 'var(--r)', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)' }}>🛡️ Insurance Plans</div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {[
                      { val: 'standard', label: '📋 Standard Plans',         desc: 'Use the global insurance options from Booking Setup' },
                      { val: 'custom',   label: '✏️ Custom for this model',  desc: 'Define plans specifically for vehicles in this model' },
                    ].map(opt => (
                      <div key={opt.val} onClick={() => setModelForm(p => ({ ...p, insuranceMode: opt.val }))} style={{
                        border: `2px solid ${modelForm.insuranceMode === opt.val ? 'var(--ac)' : 'var(--bd)'}`,
                        borderRadius: 'var(--r)', padding: '8px 10px', cursor: 'pointer',
                        background: modelForm.insuranceMode === opt.val ? 'var(--ac-l)' : 'var(--sur)',
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 600 }}>{opt.label}</div>
                        <div style={{ fontSize: 9, color: 'var(--tx3)', marginTop: 2 }}>{opt.desc}</div>
                      </div>
                    ))}
                  </div>

                  {modelForm.insuranceMode === 'custom' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

                      {(modelForm.customInsurance || []).map((plan, idx) => (
                        insPlanEdit === idx ? (
                          <InsPlanFormCard key={idx} planForm={insPlanForm} setPlanForm={setInsPlanForm} onSave={saveInsPlan} onCancel={() => setInsPlanEdit(null)} />
                        ) : (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', background: 'var(--sur2)', border: `1.5px solid ${plan.color || 'var(--bd)'}`, borderRadius: 'var(--r)' }}>
                            <span style={{ fontSize: 22 }}>{plan.icon}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ color: plan.color }}>{plan.name}</span>
                                {plan.recommended && (
                                  <span style={{ fontSize: 8, background: plan.color, color: '#fff', padding: '1px 5px', borderRadius: 8, fontWeight: 700 }}>Recommended</span>
                                )}
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--tx3)', marginTop: 1 }}>
                                {parseFloat(plan.price) > 0 ? `€${plan.price}/day` : 'Included'} · Excess: {plan.excess || '—'} · Deposit: €{plan.deposit || 0}
                              </div>
                              {plan.desc && <div style={{ fontSize: 9, color: 'var(--tx3)', marginTop: 1 }}>{plan.desc}</div>}
                            </div>
                            <button className="btn btn-ghost btn-sm" onClick={() => openEditInsPlan(idx)}>Edit</button>
                            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--ac)' }} onClick={() => deleteInsPlan(idx)}>✕</button>
                          </div>
                        )
                      ))}

                      {insPlanEdit === 'new'
                        ? <InsPlanFormCard planForm={insPlanForm} setPlanForm={setInsPlanForm} onSave={saveInsPlan} onCancel={() => setInsPlanEdit(null)} />
                        : <button className="btn btn-ghost btn-sm" onClick={openNewInsPlan} style={{ alignSelf: 'flex-start', marginTop: 2 }}>+ Add Insurance Plan</button>
                      }

                      {(modelForm.customInsurance || []).length === 0 && insPlanEdit !== 'new' && (
                        <div style={{ fontSize: 10, color: 'var(--tx3)', fontStyle: 'italic' }}>
                          No plans yet — global standard plans will be shown until you add at least one custom plan.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </FormCard>
          )}

          {vehicleModels.length === 0 && !showModelForm ? (
            <EmptyState icon="🏷️" title="No models yet"
              sub="Create models (e.g. Toyota Corolla) so customers see a clean booking page without plate numbers."
              action="Add First Model" onAction={openAddModel} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
              {vehicleModels.map(m => {
                const assignedVehicles = vehicles.filter(v => v.modelId === m.id);
                const cnt = assignedVehicles.length;
                const cover = (m.photos || [])[0];
                const isExpanded = expandedModelId === m.id;
                return (
                  <div key={m.id} style={{
                    background: 'var(--sur)', border: `1px solid ${isExpanded ? 'var(--ac)' : 'var(--bd)'}`,
                    borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                    gridColumn: isExpanded ? '1 / -1' : 'auto',
                    transition: 'border-color .15s',
                  }}>
                    <div style={{ display: 'flex', flexDirection: isExpanded ? 'row' : 'column' }}>
                      <div style={{ height: isExpanded ? 'auto' : 110, minHeight: isExpanded ? 160 : undefined, width: isExpanded ? 220 : '100%', flexShrink: 0, background: cover ? '#000' : 'var(--sur2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>
                        {cover
                          ? <img src={cover} alt={m.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span>{m.icon || '🚗'}</span>
                        }
                      </div>
                      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{m.icon} {m.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--tx3)' }}>
                          {[m.transmission, m.fuelType, m.seats, m.mileage].filter(Boolean).join(' · ')}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--bl)', fontFamily: "'DM Mono',monospace" }}>
                          €{m.priceDay || 0}/day {m.deposit ? `· deposit €${m.deposit}` : ''}
                        </div>
                        <button
                          onClick={() => setExpandedModelId(isExpanded ? null : m.id)}
                          style={{
                            background: 'none', border: 'none', padding: 0, textAlign: 'left',
                            fontSize: 10, color: cnt > 0 ? 'var(--ac)' : 'var(--tx3)', marginTop: 2,
                            cursor: cnt > 0 ? 'pointer' : 'default',
                            textDecoration: cnt > 0 ? 'underline' : 'none',
                          }}
                          disabled={cnt === 0}
                        >
                          {isExpanded ? '▼' : '▶'} {cnt} vehicle{cnt !== 1 ? 's' : ''} assigned · {m.confirm === 'auto' ? '⚡ Auto-confirm' : '✋ Manual confirm'}
                        </button>
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => openEditModel(m)}>Edit</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: cnt > 0 ? 'var(--tx3)' : 'var(--ac)' }}
                            onClick={() => deleteModel(m.id)} title={cnt > 0 ? `${cnt} vehicle(s) use this model` : 'Delete'}>✕</button>
                        </div>
                      </div>
                    </div>

                    {/* Expanded — assigned vehicles list */}
                    {isExpanded && (
                      <div style={{ borderTop: '1px solid var(--bd)', padding: '10px 12px', background: 'var(--sur2)' }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx2)', marginBottom: 8 }}>
                          🚗 Assigned vehicles ({cnt})
                        </div>
                        {cnt === 0 ? (
                          <div style={{ fontSize: 11, color: 'var(--tx3)', fontStyle: 'italic' }}>No vehicles assigned to this model yet.</div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                            {assignedVehicles.map(v => (
                              <div key={v.id} style={{
                                background: 'var(--sur)', border: '1px solid var(--bd)',
                                borderRadius: 8, padding: '8px 10px',
                                display: 'flex', alignItems: 'center', gap: 8,
                              }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {v.name || v.plate}
                                  </div>
                                  <div style={{ fontSize: 9, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>
                                    {v.plate} · <span className={`sp s-${v.status?.slice(0, 2)}`} style={{ fontSize: 8 }}>{v.status}</span>
                                  </div>
                                </div>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => openModal('add-vehicle', v.id)}
                                  title="Edit vehicle"
                                  style={{ padding: '3px 6px', fontSize: 10 }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => unassignVehicleFromModel(v.id)}
                                  title="Unassign from this model"
                                  style={{ padding: '3px 6px', fontSize: 10, color: 'var(--ac)' }}
                                >
                                  Unassign
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ GROUPS TAB ═════════════════════════════ */}
      {tab === 'groups' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Vehicle Groups</div>
              <div style={{ fontSize: 11, color: 'var(--tx2)', marginTop: 2 }}>Group vehicles to manage pricing at scale. Each vehicle can use the group price or its own individual price.</div>
            </div>
            {!showGroupForm && (
              <button className="btn btn-primary" style={{ flexShrink: 0, marginLeft: 16 }} onClick={openAddGroup}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New Group
              </button>
            )}
          </div>

          {showGroupForm && (
            <FormCard title={editingGroupId ? '✏️ Edit Group' : '➕ New Group'} onCancel={cancelGroupForm} onSave={saveGroup} saveLabel={editingGroupId ? 'Save Changes' : 'Create Group'}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.8 }}>Icon</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {GROUP_ICONS.map(ic => (
                      <div key={ic} onClick={() => setGroupForm(f => ({ ...f, icon: ic }))} style={{ fontSize: 18, padding: '5px 8px', borderRadius: 7, cursor: 'pointer', border: `2px solid ${groupForm.icon === ic ? 'var(--ac)' : 'var(--bd)'}`, background: groupForm.icon === ic ? 'var(--ac-l)' : 'var(--sur)' }}>{ic}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--tx3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.8 }}>Color</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {COLORS.map(c => (
                      <div key={c} onClick={() => setGroupForm(f => ({ ...f, color: c }))} style={{ width: 24, height: 24, borderRadius: '50%', background: c, cursor: 'pointer', border: `3px solid ${groupForm.color === c ? 'var(--tx)' : 'transparent'}`, boxSizing: 'border-box' }} />
                    ))}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="fg">
                    <label className="fl">Group Name</label>
                    <input type="text" className="fi" placeholder="e.g. Economy, Premium, Beach Cars..." value={groupForm.name} onChange={e => setGroupForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="fg">
                    <label className="fl">Base Price / Day (€)</label>
                    <input type="number" className="fi" placeholder="e.g. 45" value={groupForm.priceDay} onChange={e => setGroupForm(f => ({ ...f, priceDay: e.target.value }))} />
                  </div>
                  <div className="fg">
                    <label className="fl">Min Rental Days</label>
                    <input type="number" className="fi" min="1" placeholder="1" value={groupForm.minDays} onChange={e => setGroupForm(f => ({ ...f, minDays: e.target.value }))} />
                  </div>
                </div>
                <Toggle value={groupForm.showOnBooking} onChange={v => setGroupForm(f => ({ ...f, showOnBooking: v }))} label="Show as filter on booking page" />
                {agreementTemplates.length > 0 && (
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Agreement Template</label>
                    <select className="fsel2" value={groupForm.agreementId} onChange={e => setGroupForm(f => ({ ...f, agreementId: e.target.value }))}>
                      <option value="">— None (use default)</option>
                      {agreementTemplates.map(t => (
                        <option key={t.id} value={t.id}>{t.icon} {t.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </FormCard>
          )}

          {groups.length === 0 && !showGroupForm ? (
            <EmptyState icon="📁" title="No groups yet" sub="Create groups to manage pricing for multiple vehicles at once" action="Create First Group" onAction={openAddGroup} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
              {groups.map(g => {
                const vCount = vehiclesInGroup(g.id);
                return (
                  <div key={g.id} style={{ background: 'var(--sur)', border: `1.5px solid ${editingGroupId === g.id ? g.color : 'var(--bd)'}`, borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--sh)' }}>
                    <div style={{ height: 5, background: g.color }} />
                    <div style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: 26 }}>{g.icon}</span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700 }}>{g.name}</div>
                          <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{vCount} vehicle{vCount !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--bl)', fontFamily: "'DM Mono',monospace", marginBottom: 4 }}>
                        €{g.priceDay}<span style={{ fontSize: 10, fontWeight: 400, color: 'var(--tx3)' }}>/day</span>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 6 }}>
                        Min {g.minDays ?? 1} day{(g.minDays ?? 1) !== 1 ? 's' : ''}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4 }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: g.showOnBooking ? 'var(--gr)' : 'var(--tx3)' }} />
                        <span style={{ fontSize: 10, color: 'var(--tx3)' }}>{g.showOnBooking ? 'On booking page' : 'Hidden'}</span>
                      </div>
                      {(() => {
                        const agrTmpl = g.agreementId ? agreementTemplates.find(t => t.id === g.agreementId) : null;
                        return agrTmpl ? (
                          <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 12 }}>
                            📋 {agrTmpl.icon} {agrTmpl.name}
                          </div>
                        ) : (
                          <div style={{ fontSize: 10, color: 'var(--tx3)', marginBottom: 12, fontStyle: 'italic' }}>
                            No agreement assigned
                          </div>
                        );
                      })()}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-xs" style={{ flex: 1, justifyContent: 'center' }} onClick={() => openEditGroup(g)}>Edit</button>
                        <button className="btn btn-xs" style={{ flex: 1, justifyContent: 'center', background: '#fee2e2', color: '#ef4444', border: '1px solid #fca5a5' }} onClick={() => deleteGroup(g.id)}>Delete</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ TYPES TAB ══════════════════════════════ */}
      {tab === 'types' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Vehicle Types</div>
              <div style={{ fontSize: 11, color: 'var(--tx3)', marginTop: 2 }}>Create custom types for your fleet — used in filters and the booking page</div>
            </div>
            {!showTypeForm && (
              <button className="btn btn-primary" style={{ flexShrink: 0, marginLeft: 16 }} onClick={openAddType}>+ Add Type</button>
            )}
          </div>

          {showTypeForm && (
            <FormCard title={editingTypeId ? '✏️ Edit Type' : '➕ New Type'} onCancel={cancelTypeForm} onSave={saveType} saveLabel={editingTypeId ? 'Save Changes' : 'Create Type'}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="fg" style={{ margin: 0 }}>
                  <label className="fl">Type name</label>
                  <input type="text" className="fi" placeholder="e.g. Motorbike, Boat, Camper…"
                    value={typeForm.name} onChange={e => setTypeForm(f => ({ ...f, name: e.target.value }))} />
                </div>
                <div className="fg" style={{ margin: 0 }}>
                  <label className="fl">Icon</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {TYPE_ICONS.map(ic => (
                      <div key={ic} onClick={() => setTypeForm(f => ({ ...f, icon: ic }))}
                        style={{ fontSize: 20, padding: '4px 8px', borderRadius: 7, cursor: 'pointer',
                          border: `2px solid ${typeForm.icon === ic ? 'var(--ac)' : 'var(--bd)'}`,
                          background: typeForm.icon === ic ? 'var(--ac-l)' : 'var(--sur)' }}>
                        {ic}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </FormCard>
          )}

          {vehicleTypes.length === 0 && !showTypeForm ? (
            <EmptyState icon="🏷️" title="No custom types yet" sub="Add vehicle types to organise your fleet" action="Add First Type" onAction={openAddType} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
              {vehicleTypes.map(t => {
                const cnt = vehicles.filter(v => v.type === t.name).length;
                const isEditing = editingTypeId === t.id;
                return (
                  <div key={t.id} style={{
                    background: 'var(--sur)', border: `1px solid ${isEditing ? 'var(--ac)' : 'var(--bd)'}`,
                    borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', textAlign: 'center',
                  }}>
                    <div style={{ fontSize: 32 }}>{t.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{cnt} vehicle{cnt !== 1 ? 's' : ''}</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEditType(t)}>Edit</button>
                      <button className="btn btn-ghost btn-sm" style={{ color: cnt > 0 ? 'var(--tx3)' : 'var(--ac)' }}
                        onClick={() => deleteType(t.id)} title={cnt > 0 ? `${cnt} vehicle(s) using this type` : 'Delete'}>✕</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ RATE RULES TAB ══════════════════════════ */}
      {tab === 'rules' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Rate Rules</div>
              <div style={{ fontSize: 11, color: 'var(--tx2)', marginTop: 2 }}>Automatic discounts or surcharges based on rental duration or date range. Rules stack on top of the base price.</div>
            </div>
            {!showRuleForm && (
              <button className="btn btn-primary" style={{ flexShrink: 0, marginLeft: 16 }} onClick={openAddRule}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New Rule
              </button>
            )}
          </div>

          {showRuleForm && (
            <FormCard title={editingRuleId ? '✏️ Edit Rate Rule' : '➕ New Rate Rule'} onCancel={cancelRuleForm} onSave={saveRule} saveLabel={editingRuleId ? 'Save Changes' : 'Create Rule'}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="fg">
                    <label className="fl">Rule Name</label>
                    <input type="text" className="fi" placeholder="e.g. Week Discount, High Season..." value={ruleForm.name} onChange={e => setRuleForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="fg">
                    <label className="fl">Applies To</label>
                    <select className="fsel2" value={ruleForm.appliesTo} onChange={e => setRuleForm(f => ({ ...f, appliesTo: e.target.value }))}>
                      <option value="all">All vehicles</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.icon} {g.name} (group)</option>)}
                    </select>
                  </div>
                </div>

                <div className="fg">
                  <label className="fl">Condition Type</label>
                  <select className="fsel2" value={ruleForm.type} onChange={e => setRuleForm(f => ({ ...f, type: e.target.value }))}>
                    <option value="duration">Duration — minimum number of days</option>
                    <option value="daterange">Date range — specific dates only</option>
                    <option value="both">Both — duration AND date range must match</option>
                  </select>
                </div>

                {(ruleForm.type === 'duration' || ruleForm.type === 'both') && (
                  <div className="fg">
                    <label className="fl">Minimum days</label>
                    <input type="number" className="fi" placeholder="e.g. 7" min="1" value={ruleForm.minDays} onChange={e => setRuleForm(f => ({ ...f, minDays: e.target.value }))} />
                  </div>
                )}

                {(ruleForm.type === 'daterange' || ruleForm.type === 'both') && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="fg">
                      <label className="fl">From date</label>
                      <input type="date" className="fi" value={ruleForm.dateStart} onChange={e => setRuleForm(f => ({ ...f, dateStart: e.target.value }))} />
                    </div>
                    <div className="fg">
                      <label className="fl">To date</label>
                      <input type="date" className="fi" value={ruleForm.dateEnd} onChange={e => setRuleForm(f => ({ ...f, dateEnd: e.target.value }))} />
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="fg">
                    <label className="fl">Effect</label>
                    <select className="fsel2" value={ruleForm.effect} onChange={e => setRuleForm(f => ({ ...f, effect: e.target.value }))}>
                      <option value="discount">Discount — reduce price</option>
                      <option value="surcharge">Surcharge — increase price</option>
                    </select>
                  </div>
                  <div className="fg">
                    <label className="fl">Amount</label>
                    <input type="number" className="fi" placeholder="e.g. 20" min="0" value={ruleForm.value} onChange={e => setRuleForm(f => ({ ...f, value: e.target.value }))} />
                  </div>
                  <div className="fg">
                    <label className="fl">Unit</label>
                    <select className="fsel2" value={ruleForm.valueType} onChange={e => setRuleForm(f => ({ ...f, valueType: e.target.value }))}>
                      <option value="percent">% (percent)</option>
                      <option value="fixed">€ (fixed per day)</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'center' }}>
                  <div className="fg" style={{ margin: 0 }}>
                    <label className="fl">Customer label (optional)</label>
                    <input type="text" className="fi" placeholder="e.g. Week discount, High season..." value={ruleForm.label} onChange={e => setRuleForm(f => ({ ...f, label: e.target.value }))} />
                  </div>
                  <div style={{ paddingTop: 18 }}>
                    <Toggle value={ruleForm.showLabel} onChange={v => setRuleForm(f => ({ ...f, showLabel: v }))} label="Show to customer" />
                  </div>
                </div>

                <Toggle value={ruleForm.active} onChange={v => setRuleForm(f => ({ ...f, active: v }))} label="Rule is active" />
              </div>
            </FormCard>
          )}

          {rateRules.length === 0 && !showRuleForm ? (
            <EmptyState icon="📊" title="No rate rules yet" sub="Create rules to automatically apply discounts or seasonal surcharges" action="Create First Rule" onAction={openAddRule} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {rateRules.map(r => (
                <div key={r.id} style={{
                  background: 'var(--sur)', border: `1px solid ${editingRuleId === r.id ? 'var(--ac)' : 'var(--bd)'}`,
                  borderRadius: 10, padding: '14px 18px', opacity: r.active ? 1 : 0.55,
                  display: 'flex', alignItems: 'center', gap: 16,
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{r.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)', background: 'var(--sur2)', borderRadius: 4, padding: '1px 6px' }}>{ruleAppliesToLabel(r)}</div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--tx2)' }}>
                      {ruleConditionLabel(r)}
                      {r.label && r.showLabel && <span style={{ color: 'var(--tx3)', marginLeft: 8 }}>· label: "{r.label}"</span>}
                    </div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 800, fontFamily: "'DM Mono',monospace", color: r.effect === 'discount' ? 'var(--gr)' : '#ef4444', flexShrink: 0 }}>
                    {ruleEffectLabel(r)}
                  </div>
                  <Toggle value={r.active} onChange={() => toggleRuleActive(r.id)} />
                  <button className="btn btn-ghost btn-xs" onClick={() => openEditRule(r)}>Edit</button>
                  <button className="btn btn-xs" style={{ background: '#fee2e2', color: '#ef4444', border: '1px solid #fca5a5' }} onClick={() => deleteRule(r.id)}>Delete</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════ PROMO CODES TAB ════════════════════════ */}
      {tab === 'promos' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Promo Codes</div>
              <div style={{ fontSize: 11, color: 'var(--tx2)', marginTop: 2 }}>Custom discount codes for campaigns, individual customers, or refund replacements.</div>
            </div>
            {!showPromoForm && (
              <button className="btn btn-primary" style={{ flexShrink: 0, marginLeft: 16 }} onClick={openAddPromo}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                New Promo Code
              </button>
            )}
          </div>

          {showPromoForm && (
            <FormCard title={editingPromoId ? '✏️ Edit Promo Code' : '➕ New Promo Code'} onCancel={cancelPromoForm} onSave={savePromo} saveLabel={editingPromoId ? 'Save Changes' : 'Create Code'}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="fg">
                    <label className="fl">Promo Code</label>
                    <input type="text" className="fi" placeholder="e.g. SUMMER30" value={promoForm.code} onChange={e => setPromoForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} style={{ fontFamily: "'DM Mono',monospace", letterSpacing: 1 }} />
                  </div>
                  <div className="fg">
                    <label className="fl">Discount Type</label>
                    <select className="fsel2" value={promoForm.discountType} onChange={e => setPromoForm(f => ({ ...f, discountType: e.target.value }))}>
                      <option value="percent">% off (percentage)</option>
                      <option value="fixed">€ off (fixed amount)</option>
                    </select>
                  </div>
                  <div className="fg">
                    <label className="fl">Discount Value ({promoForm.discountType === 'percent' ? '%' : '€'})</label>
                    <input type="number" className="fi" placeholder={promoForm.discountType === 'percent' ? 'e.g. 20' : 'e.g. 50'} min="0" value={promoForm.discountValue} onChange={e => setPromoForm(f => ({ ...f, discountValue: e.target.value }))} />
                  </div>
                  <div className="fg">
                    <label className="fl">Usage Limit (blank = unlimited)</label>
                    <input type="number" className="fi" placeholder="Leave blank for unlimited" min="1" value={promoForm.usageLimit} onChange={e => setPromoForm(f => ({ ...f, usageLimit: e.target.value }))} />
                  </div>
                  <div className="fg">
                    <label className="fl">Expiry Date (optional)</label>
                    <input type="date" className="fi" value={promoForm.expiryDate} onChange={e => setPromoForm(f => ({ ...f, expiryDate: e.target.value }))} />
                  </div>
                  <div className="fg">
                    <label className="fl">Internal Note (optional)</label>
                    <input type="text" className="fi" placeholder="e.g. Refund replacement for John" value={promoForm.note} onChange={e => setPromoForm(f => ({ ...f, note: e.target.value }))} />
                  </div>
                </div>
                <Toggle value={promoForm.active} onChange={v => setPromoForm(f => ({ ...f, active: v }))} label="Code is active" />
              </div>
            </FormCard>
          )}

          {promoCodes.length === 0 && !showPromoForm ? (
            <EmptyState icon="🎟️" title="No promo codes yet" sub="Create discount codes for promotions or individual customer offers" action="Create First Code" onAction={openAddPromo} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {promoCodes.map(p => {
                const usageStr = p.usageLimit ? `${p.usedCount}/${p.usageLimit}` : `${p.usedCount}/∞`;
                const discountStr = p.discountType === 'percent' ? `${p.discountValue}% off` : `€${p.discountValue} off`;
                const expired = p.expiryDate && new Date(p.expiryDate) < new Date();
                const exhausted = p.usageLimit && p.usedCount >= p.usageLimit;
                return (
                  <div key={p.id} style={{
                    background: 'var(--sur)', border: `1px solid ${editingPromoId === p.id ? 'var(--ac)' : 'var(--bd)'}`,
                    borderRadius: 10, padding: '14px 18px',
                    display: 'flex', alignItems: 'center', gap: 16,
                    opacity: p.active && !expired && !exhausted ? 1 : 0.55,
                  }}>
                    <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: 'var(--tx)', minWidth: 120 }}>{p.code}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gr)', minWidth: 80 }}>{discountStr}</div>
                    <div style={{ fontSize: 11, color: 'var(--tx2)', minWidth: 60 }}>Used: {usageStr}</div>
                    <div style={{ fontSize: 11, color: expired ? '#ef4444' : 'var(--tx2)', minWidth: 90 }}>
                      {p.expiryDate ? (expired ? `⚠️ ${p.expiryDate}` : p.expiryDate) : 'No expiry'}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--tx3)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.note || '—'}</div>
                    <Toggle value={p.active} onChange={() => togglePromoActive(p.id)} />
                    <button className="btn btn-ghost btn-xs" onClick={() => openEditPromo(p)}>Edit</button>
                    <button className="btn btn-xs" style={{ background: '#fee2e2', color: '#ef4444', border: '1px solid #fca5a5' }} onClick={() => deletePromo(p.id)}>Delete</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === 'transfers' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>📍 Transfer History</h3>
            <p style={{ fontSize: 12, color: 'var(--tx3)', margin: '4px 0 0' }}>Log of all vehicle movements between areas — automatic (rental returns) and manual transfers.</p>
          </div>

          {locationLog.length === 0 ? (
            <EmptyState icon="📍" title="No transfers yet" sub="Vehicle movements between areas will appear here" />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {locationLog.map((entry) => {
                const veh = vehicles.find(v => v.id === entry.vid);
                const fromArea = areas.find(a => a.id === entry.fromAreaId);
                const toArea = areas.find(a => a.id === entry.toAreaId);
                const date = new Date(entry.timestamp);
                const fmtDate = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                const fmtTime = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                const isReturn = entry.reason === 'rental_return';
                return (
                  <div key={entry.id} style={{
                    background: 'var(--sur)', border: '1px solid var(--bd)', borderRadius: 10,
                    padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14,
                  }}>
                    {/* Icon */}
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: isReturn ? 'var(--bl-l)' : 'var(--am-l)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
                    }}>
                      {isReturn ? '🚗' : '🔄'}
                    </div>
                    {/* Vehicle */}
                    <div style={{ minWidth: 120 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{veh?.icon} {veh?.name?.split('–')[0]?.trim() || entry.vid}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace" }}>{veh?.plate || ''}</div>
                    </div>
                    {/* From → To */}
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                        background: fromArea?.color ? fromArea.color + '18' : 'var(--sur2)',
                        color: fromArea?.color || 'var(--tx2)',
                      }}>
                        {fromArea ? `${fromArea.icon} ${fromArea.name}` : '—'}
                      </span>
                      <span style={{ fontSize: 13, color: 'var(--tx3)' }}>→</span>
                      <span style={{
                        padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                        background: toArea?.color ? toArea.color + '18' : 'var(--sur2)',
                        color: toArea?.color || 'var(--tx2)',
                      }}>
                        {toArea ? `${toArea.icon} ${toArea.name}` : '—'}
                      </span>
                    </div>
                    {/* Reason */}
                    <div style={{ minWidth: 110 }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                        background: isReturn ? '#dbeafe' : '#fef3c7',
                        color: isReturn ? '#1d4ed8' : '#92400e',
                      }}>
                        {isReturn ? 'Rental Return' : 'Manual Transfer'}
                      </span>
                      {entry.rentalId && (
                        <div style={{ fontSize: 10, color: 'var(--tx3)', fontFamily: "'DM Mono',monospace", marginTop: 2 }}>
                          {entry.rentalId}
                        </div>
                      )}
                    </div>
                    {/* Date */}
                    <div style={{ minWidth: 100, textAlign: 'right' }}>
                      <div style={{ fontSize: 11, fontWeight: 500 }}>{fmtDate}</div>
                      <div style={{ fontSize: 10, color: 'var(--tx3)' }}>{fmtTime}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
